import { useEffect } from "react";
import {
  BlockStack,
  InlineStack,
  Text,
  ProgressBar,
  Badge,
  Box,
  Button,
} from "@medentic/polaris-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ProcessData, ProcessStatus } from "@shared/types/processTypes";

interface ProcessLoadingProps {
  processId: string;
  /** Estimated total time in seconds (from server response) */
  estimatedTimeSeconds?: number;
  /** Polling interval in milliseconds (default: 2000) */
  refreshTime?: number;
  /** Called when process completes successfully, with output data */
  onComplete?: (output?: Record<string, unknown>) => void;
  onError?: (error: string) => void;
  /** Called when user clicks retry after a failure */
  onRetry?: () => void;
}

const TERMINAL_STATUSES = [
  ProcessStatus.COMPLETED,
  ProcessStatus.FAILED,
  ProcessStatus.EXCEEDED_RETRIES,
];

function getStatusBadgeTone(
  status: ProcessStatus
): "info" | "success" | "warning" | "critical" | "attention" {
  switch (status) {
    case ProcessStatus.PENDING:
    case ProcessStatus.QUEUED:
      return "attention";
    case ProcessStatus.IN_PROGRESS:
      return "info";
    case ProcessStatus.COMPLETED:
      return "success";
    case ProcessStatus.FAILED:
    case ProcessStatus.EXCEEDED_RETRIES:
      return "critical";
    default:
      return "info";
  }
}

function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

export default function ProcessLoading({
  processId,
  estimatedTimeSeconds,
  refreshTime = 2000,
  onComplete,
  onError,
  onRetry,
}: ProcessLoadingProps) {
  const { t } = useTranslation();
  const { GET } = useApiRequest();

  const { data: process, error } = useQuery<ProcessData>({
    queryKey: ["process", processId],
    queryFn: () => GET<ProcessData>(`/api/processes/${processId}`),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && TERMINAL_STATUSES.includes(data.status)) {
        return false; // Stop polling
      }
      return refreshTime;
    },
    enabled: Boolean(processId),
  });

  // Handle completion and error callbacks
  useEffect(() => {
    if (!process) return;

    if (process.status === ProcessStatus.COMPLETED) {
      onComplete?.(process.output);
    } else if (
      process.status === ProcessStatus.FAILED ||
      process.status === ProcessStatus.EXCEEDED_RETRIES
    ) {
      const errorMessage =
        typeof process.output?.error === "string"
          ? process.output.error
          : t("process.status.failed");
      onError?.(errorMessage);
    }
  }, [process?.status, onComplete, onError, process, t]);

  // Handle query error
  useEffect(() => {
    if (error) {
      onError?.(t("process.fetchError"));
    }
  }, [error, onError, t]);

  if (!process) {
    return (
      <Box padding="400">
        <BlockStack gap="300">
          <ProgressBar progress={0} size="small" />
          <Text variant="bodySm" tone="subdued" as="p">
            {t("process.loading")}
          </Text>
        </BlockStack>
      </Box>
    );
  }

  // Calculate remaining time based on server's estimated total time and current progress
  // remainingTime = totalTime * (100 - progress) / 100
  const estimatedRemaining =
    estimatedTimeSeconds && estimatedTimeSeconds > 0
      ? (estimatedTimeSeconds * (100 - process.progress)) / 100
      : 0;

  const isFailed =
    process.status === ProcessStatus.FAILED ||
    process.status === ProcessStatus.EXCEEDED_RETRIES;

  return (
    <Box padding="400">
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Badge tone={getStatusBadgeTone(process.status)}>
            {t(`process.status.${process.status}`)}
          </Badge>
          <Text variant="bodySm" tone="subdued" as="span">
            {process.progress.toFixed(0)}%
          </Text>
        </InlineStack>

        <ProgressBar
          progress={process.progress}
          size="small"
          tone={isFailed ? "critical" : "primary"}
        />

        {!TERMINAL_STATUSES.includes(process.status) &&
          estimatedRemaining > 0 && (
            <Text variant="bodySm" tone="subdued" as="p">
              {t("process.estimatedTime", {
                time: formatTimeRemaining(estimatedRemaining),
              })}
            </Text>
          )}

        {process.status === ProcessStatus.COMPLETED && (
          <Text variant="bodySm" tone="success" as="p">
            {t("process.completed")}
          </Text>
        )}

        {isFailed && (
          <BlockStack gap="200">
            <Text variant="bodySm" tone="critical" as="p">
              {typeof process.output?.error === "string"
                ? process.output.error
                : t("process.failedMessage")}
            </Text>
            {onRetry && (
              <Button onClick={onRetry} size="slim">
                {t("process.retry")}
              </Button>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Box>
  );
}

